import React, {ReactNode} from 'react';
import {FieldValues} from 'react-hook-form';
import {t} from '@lingui/core/macro';

import Alert from 'shared/ui/Alert/Alert';
import ModalBodyWrapper from 'shared/ui/Modal/ModalBodyWrapper';
import ModalBlockButton from 'shared/ui/Button/ModalBlockButton/ModalBlockButton';
import FormGroupWithError from 'shared/ui/FormGroup/FormGroupWithError';

import {IMassOperationAction} from 'shared/ui/MassOperations/MassOperations';

import TagsChipField from '../TagsChipField/TagsChipField';
import ExportResult from '../ExportResult/ExportResult';
import {isRemoveByListAction} from '../../lib/batchOperation';
import {IMonitorBatchOperationsFormState} from '../../model/useMonitorBatchOperationsForm';

import BatchResult from '../BatchResult/BatchResult';
import BatchOperationSummary from '../BatchOperationSummary/BatchOperationSummary';
import AddFieldDropdown, {IAddFieldOption} from '../BatchEdit/AddFieldDropdown';

import styles from './MonitorBatchOperationsForm.module.scss';

export interface IRemoveByListConfig {
    label: string,
    hint: string,
    error?: string,
    field: ReactNode,
}

export interface IBatchEditConfig {
    fields: IAddFieldOption[],
    renderField: (_key: string) => ReactNode,
}

interface IMonitorBatchOperationsFormProps<T extends FieldValues> {
    action?: IMassOperationAction,
    state: IMonitorBatchOperationsFormState<T>,
    edit?: IBatchEditConfig,
    selectedLabel: string,
    removeByList?: IRemoveByListConfig,
    exportCountLabel?: string,
}

function MonitorBatchOperationsForm<T extends FieldValues>({
    action,
    state,
    edit,
    selectedLabel,
    removeByList,
    exportCountLabel,
}: IMonitorBatchOperationsFormProps<T>) {
    const {
        form,
        user,
        result,
        isLoading,
        progress,
        actionValue,
        isEdit,
        isTags,
        selectedCount,
        addedFields,
        addField,
        editError,
        tagsError,
        isSubmitDisabled,
        resultTitle,
        resultCountLabel,
        submitText,
        submit,
        handleCloseResult,
        handleCancel,
    } = state;

    if (result) {
        return actionValue === 'export' && exportCountLabel ? (
            <ExportResult result={result} countLabel={exportCountLabel} handleClose={handleCloseResult}/>
        ) : (
            <BatchResult
                result={result}
                handleClose={handleCloseResult}
                title={resultTitle}
                countLabel={resultCountLabel}
            />
        );
    }

    const renderBody = () => {
        if (isEdit && edit) {
            return (
                <>
                    {addedFields.length === 0 && (
                        <div className={styles.batchEmpty}>
                            {t`Добавьте поля, которые нужно изменить. Изменятся только добавленные поля.`}
                        </div>
                    )}
                    {addedFields.map(edit.renderField)}
                    {editError && <Alert>{editError}</Alert>}
                    <AddFieldDropdown
                        addedFields={addedFields}
                        user={user}
                        fields={edit.fields}
                        onAdd={addField}
                    />
                </>
            );
        }

        if (isTags) {
            return <TagsChipField control={form.control} error={tagsError}/>;
        }

        if (removeByList && isRemoveByListAction(actionValue)) {
            return (
                <>
                    <FormGroupWithError label={removeByList.label} error={removeByList.error}>
                        {removeByList.field}
                    </FormGroupWithError>
                    <span className={styles.hint}>{removeByList.hint}</span>
                </>
            );
        }

        return (
            <BatchOperationSummary
                selectedLabel={selectedLabel}
                selectedCount={selectedCount}
                actionLabel={action?.label}
            />
        );
    };

    return (
        <form onSubmit={submit} className={styles.form}>
            <ModalBodyWrapper>
                {renderBody()}
            </ModalBodyWrapper>
            <ModalBlockButton
                primaryButtonText={submitText}
                primaryButtonType="submit"
                primaryButtonLoading={isLoading}
                primaryButtonDisabled={isSubmitDisabled}
                secondaryButtonOnClick={handleCancel}
                progress={progress}
            />
        </form>
    );
}

export default MonitorBatchOperationsForm;
